import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Trail Marker / Return-to-Point — user taps "Mark this spot" → tool
// records current lat/lng. Then continuously shows distance + bearing
// back to that point.
//
// Distance uses the haversine formula (great-circle distance, accurate
// at all scales relevant to a phone).
//
// Bearing uses the forward-azimuth great-circle formula. Combined with
// the device heading (compass), the on-screen arrow points "where you
// need to walk to get back."
//
// State is session-only (per brief: refresh clears).

const R_EARTH_M = 6371000;
const deg2rad = (d) => (d * Math.PI) / 180;

const haversine = (lat1, lng1, lat2, lng2) => {
  const phi1 = deg2rad(lat1), phi2 = deg2rad(lat2);
  const dphi = deg2rad(lat2 - lat1);
  const dlam = deg2rad(lng2 - lng1);
  const a = Math.sin(dphi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dlam / 2) ** 2;
  return 2 * R_EARTH_M * Math.asin(Math.sqrt(a));
};

const bearing = (lat1, lng1, lat2, lng2) => {
  const phi1 = deg2rad(lat1), phi2 = deg2rad(lat2);
  const dlam = deg2rad(lng2 - lng1);
  const y = Math.sin(dlam) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dlam);
  const theta = Math.atan2(y, x);
  return ((theta * 180) / Math.PI + 360) % 360;
};

const fmtDistance = (m) => {
  if (m < 1) return "0 m";
  if (m < 1000) return `${m.toFixed(0)} m`;
  return `${(m / 1000).toFixed(2)} km`;
};

export default function TrailMarker() {
  const [locState, setLocState] = useState("idle");
  const [orientState, setOrientState] = useState("idle");
  const [pos, setPos] = useState(null);
  const [mark, setMark] = useState(null);
  const [heading, setHeading] = useState(null);
  const watchRef = useRef(null);
  const handlerRef = useRef(null);

  const enable = async () => {
    if (!navigator.geolocation) { setLocState("unsupported"); return; }
    setLocState("requesting");
    watchRef.current = navigator.geolocation.watchPosition(
      (p) => {
        setPos({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy });
        setLocState("ok");
      },
      () => setLocState("denied"),
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 30000 }
    );
    // Also try to get orientation
    if (typeof DeviceOrientationEvent === "undefined") { setOrientState("unsupported"); return; }
    try {
      if (typeof DeviceOrientationEvent.requestPermission === "function") {
        const r = await DeviceOrientationEvent.requestPermission();
        if (r !== "granted") { setOrientState("denied"); return; }
      }
      handlerRef.current = (e) => {
        let h;
        if (typeof e.webkitCompassHeading === "number") h = e.webkitCompassHeading;
        else if (typeof e.alpha === "number") h = (360 - e.alpha) % 360;
        else return;
        setHeading(h);
      };
      window.addEventListener("deviceorientation", handlerRef.current, true);
      setOrientState("ok");
    } catch {
      setOrientState("denied");
    }
  };

  useEffect(() => () => {
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
    if (handlerRef.current) window.removeEventListener("deviceorientation", handlerRef.current, true);
  }, []);

  const markHere = () => { if (pos) setMark({ ...pos, when: Date.now() }); };
  const clearMark = () => setMark(null);

  const dist = pos && mark ? haversine(pos.lat, pos.lng, mark.lat, mark.lng) : null;
  const bear = pos && mark ? bearing(pos.lat, pos.lng, mark.lat, mark.lng) : null;
  // Arrow direction relative to where the phone is pointing:
  // if compass heading is known, arrow rotation = bear - heading.
  // If unknown, arrow points to absolute north-relative bearing.
  const arrowRotation = bear != null ? (heading != null ? bear - heading : bear) : 0;

  return (
    <ToolsShell title="Trail Marker">
      {locState !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Mark your car, campsite, or trailhead. The tool will then show distance and direction back. Hiking, festivals, parking lots.
          </p>
          <button onClick={enable} style={primaryBtn} disabled={locState === "requesting"}>
            {locState === "requesting" ? "Getting GPS lock…" : "Enable location"}
          </button>
          <SensorNotice state={locState === "idle" || locState === "requesting" ? "prompt" : locState} what="Location" />
        </div>
      )}

      {locState === "ok" && (
        <>
          {!mark ? (
            <div style={{ textAlign: "center", marginTop: 10 }}>
              <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 16 }}>
                {pos
                  ? `Got a GPS fix (± ${pos.accuracy.toFixed(0)} m). When you're at the spot you want to return to, tap below.`
                  : "Waiting for GPS lock…"}
              </p>
              <button onClick={markHere} disabled={!pos} style={primaryBtn}>
                Mark this spot
              </button>
            </div>
          ) : (
            <>
              {/* Arrow indicator */}
              <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
                <svg width="260" height="260" viewBox="0 0 260 260">
                  <circle cx="130" cy="130" r="115" fill="#13141F" stroke="#262839" strokeWidth="2" />
                  {/* Cardinal letters (only N if heading-aware) */}
                  {heading != null && (
                    <g transform={`rotate(${-heading} 130 130)`}>
                      <text x="130" y="32" textAnchor="middle" fontSize="14" fontWeight="700" fill="#5B5E7A">N</text>
                    </g>
                  )}
                  {/* Arrow to the mark */}
                  <g transform={`rotate(${arrowRotation} 130 130)`}>
                    <polygon
                      points="130,30 110,150 130,135 150,150"
                      fill={TOOLS_ACCENT}
                    />
                  </g>
                  <circle cx="130" cy="130" r="6" fill="#262839" stroke={TOOLS_ACCENT} strokeWidth="2" />
                </svg>
              </div>

              {/* Distance + bearing */}
              <div style={{ background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 14, padding: "20px 22px", textAlign: "center", marginTop: 8 }}>
                <div style={{ fontSize: 12, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4 }}>Distance to mark</div>
                <div style={{ fontSize: 44, fontWeight: 800, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                  {dist != null ? fmtDistance(dist) : "—"}
                </div>
                <div style={{ fontSize: 13, color: "#9A9DBE", marginTop: 4 }}>
                  Bearing {bear != null ? `${bear.toFixed(0)}°` : "—"}
                  {heading != null && bear != null && (
                    <span> · {heading != null ? `compass at ${heading.toFixed(0)}°` : ""}</span>
                  )}
                </div>
              </div>

              {/* Status row */}
              {pos && (
                <div style={{ marginTop: 12, fontSize: 12, color: "#7C8099", textAlign: "center" }}>
                  Current GPS accuracy: ± {pos.accuracy.toFixed(0)} m
                  {orientState !== "ok" && (
                    <div style={{ marginTop: 6, color: "#D9A39E" }}>
                      Compass unavailable — arrow shows absolute bearing from north, not "point your phone."
                    </div>
                  )}
                </div>
              )}

              <button onClick={clearMark} style={{ marginTop: 18, background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "12px 18px", fontSize: 13, cursor: "pointer", width: "100%" }}>
                Clear mark
              </button>
            </>
          )}

          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center" }}>
            GPS accuracy degrades indoors, in canyons, and near tall buildings. Marker persists only for this session — don't rely on it for critical navigation.
          </p>
        </>
      )}
    </ToolsShell>
  );
}
